import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styles from './ORSMActivation.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useLoading from '../../../Base/Hooks/useLoading';
import useORSMCore from '../../Hooks/useORSMCore';
import ORSMSlugForm from '../SlugForm/ORSMSlugForm';
import {
	Subscription,
	SubscriptionMembershipType,
	SubscriptionPeriod,
	useSubscriptionMembershipCreateOrderMutation,
} from '../../../../graphql/graphql';
import useTranslation from '../../../../Hooks/useTranslation';
import useMeta from '../../../Meta/Hooks/useMeta';
import { MetaKey } from '../../../Meta/Enums/MetaKey.enum';
import { useAppContext } from '../../../../Services/AppContext';

interface Props {
	rootWorkplaceId: string;
	subscription: Subscription;
	subscriptionPeriod: SubscriptionPeriod;
}

const ORSMActivation: React.FC<Props> = ({
	rootWorkplaceId,
	subscriptionPeriod,
	subscription,
}) => {
	const formMethods = useForm();
	const { displayError, displayGQLError } = useApiClient();
	const { isLoading, setIsLoading, RenderLoading } = useLoading(false);
	const {
		isSlugAvailable,
		setIsSlugAvailable,
		checkSlugAvailability,
		isLoadingVerification,
	} = useORSMCore({
		rootWorkplaceId,
	});
	const { t } = useTranslation();
	const [createMembership] = useSubscriptionMembershipCreateOrderMutation();
	const { createWorkplaceMeta } = useMeta();
	const { isProduction } = useAppContext();

	const slug = formMethods.watch('slug');

	let domain = `https://${slug}.meddi.com`;

	if (!isProduction) {
		domain = `https://${slug}.meddi-devcal.cz`;
	}

	useEffect(() => {
		setIsSlugAvailable(false);
	}, [slug]);

	const handleActivateSubscription = async () => {
		setIsLoading(true);
		const metaResponse = await createWorkplaceMeta({
			workplaceId: rootWorkplaceId,
			meta: { key: MetaKey.PENDING_ACTIVATE_CALENDAR, value: slug },
		});
		if (metaResponse.status === 200) {
			try {
				const response = await createMembership({
					input: {
						subscriptionId: subscription.id,
						subscriptionPeriodId: subscriptionPeriod.id,
						type: SubscriptionMembershipType.Workplace,
						workplaceId: rootWorkplaceId,
					},
				});
				displayGQLError(response);

				if ('data' in response) {
					const redirectUrl =
						response.data.subscriptionMembershipCreateOrder?.redirectUrl;
					if (redirectUrl) {
						window.location.href = redirectUrl;
					}
				}
			} catch (e) {
				displayError(e);
			}
		}
		setTimeout(() => {
			setIsLoading(false);
		}, 3000);
	};

	if (isLoading) {
		return <RenderLoading />;
	}

	return (
		<div className={styles.wrapper}>
			<ORSMSlugForm
				formMethods={formMethods}
				onSlugAvailabilityCheck={() => checkSlugAvailability(slug)}
				isLoadingVerification={isLoadingVerification}
			/>
			{isSlugAvailable && (
				<>
					<div className={styles.available}>
						<img
							src='/dist/icons/subscription/circle-check-sharp-solid-full.svg'
							alt='Green check'
						/>
						<Text
							tx='component.ORSMActivation.domainIsAvailable'
							txParams={{ domain }}
						/>
					</div>

					<div className={styles.subscription}>
						<div className={styles.box1}>
							<Text
								tx='component.ORSMActivation.whatYouGet'
								className={styles.title}
							/>
							<div className={styles.item}>
								<img
									src='/dist/icons/subscription/calendar-check-regular-full.svg'
									alt='calendar'
								/>
								<div>
									<Text
										tx='component.ORSMActivation.unlimitedReservations'
										className={styles.itemTitle}
									/>
									<Text
										tx='component.ORSMActivation.noLimits'
										className={styles.itemSubtitle}
									/>
								</div>
							</div>
							<div className={styles.item}>
								<img
									src='/dist/icons/subscription/message-dots-regular-full.svg'
									alt='message'
								/>
								<div>
									<Text
										tx='component.ORSMActivation.emailNotifications'
										className={styles.itemTitle}
									/>
									<Text
										tx='component.ORSMActivation.automaticNotifications'
										className={styles.itemSubtitle}
									/>
								</div>
							</div>
							<div className={styles.item}>
								<img
									src='/dist/icons/subscription/arrows-rotate-sharp-duotone-solid-full.svg'
									alt='sync'
								/>
								<div>
									<Text
										tx='component.ORSMActivation.calendarSync'
										className={styles.itemTitle}
									/>
									<Text
										tx='component.ORSMActivation.connectionWithMeddiCalendar'
										className={styles.itemSubtitle}
									/>
								</div>
							</div>
							<div className={styles.item}>
								<img
									src='/dist/icons/subscription/money-bills-regular-full.svg'
									alt='money'
								/>
								<div>
									<Text
										tx='component.ORSMActivation.paymentsManagement'
										className={styles.itemTitle}
									/>
									<Text
										tx='component.ORSMActivation.acceptPaymentsBefore'
										className={styles.itemSubtitle}
									/>
								</div>
							</div>
						</div>
						<div className={styles.box2}>
							<Text
								tx='component.ORSMActivation.yearlySubscription'
								className={styles.title}
							/>
							<div>
								{subscriptionPeriod?.price && (
									<div className={styles.price}>
										{subscriptionPeriod.price.formattedPrice}{' '}
										<span>/ {t('base.years.one')}</span>
									</div>
								)}
								<Text
									tx='component.ORSMActivation.includingVat'
									className={styles.dph}
								/>
							</div>
							<Button
								title='component.ORSMActivation.activateSubscription'
								theme={Theme.BLUE}
								className={styles.button}
								onClick={handleActivateSubscription}
							/>
							<Text
								className={styles.agreement}
								tx='component.ORSMActivation.activationNote'
								noEscape
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default ORSMActivation;
